import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, RotateCcw, Loader2, Package } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { resolveImage } from './admin/ImageUpload';

const STATUS_COLORS = {
  pending: 'bg-neutral-200 text-neutral-700',
  confirmed: 'bg-blue-100 text-blue-700',
  shipped: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const fmt = (v) => `£${Number(v || 0).toFixed(2)}`;

/**
 * Order detail popup with Print & Reorder.
 * Print uses window.print() while the modal contents live inside a class the
 * global stylesheet targets — every other element is hidden via `@media print`.
 */
const OrderDetailDialog = ({ open, order, onClose, onReorder, reordering }) => {
  const printableRef = useRef(null);
  const { settings } = useSettings();

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !order) return null;

  // Prefer stored VAT-inclusive totals (persisted from Feb 2026 onwards).
  // Legacy orders fall back to a client-side computation from subtotal + rate.
  const legacyVatRate = Number(settings.vat_rate || 20);
  const vatRate = order.vat_rate != null ? Number(order.vat_rate) : legacyVatRate;
  const vat = order.vat_amount != null
    ? Number(order.vat_amount)
    : +(Number(order.subtotal || 0) * vatRate / 100).toFixed(2);
  const grand = order.total_inc_vat != null
    ? Number(order.total_inc_vat)
    : +(Number(order.total || 0) + vat).toFixed(2);
  const brandName = settings.brand_name || 'Dr Tools';
  const addr = order.address || {};

  const handlePrint = () => {
    document.body.classList.add('order-print-mode');
    // Small timeout to let the class take effect
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove('order-print-mode'), 200);
    }, 50);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 overflow-y-auto print-shell"
      data-testid="order-detail-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-sm max-w-3xl w-full my-8 shadow-2xl print-target"
        onClick={e => e.stopPropagation()}
        ref={printableRef}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-neutral-200 px-6 py-4 gap-4 no-print">
          <div>
            <h2 className="text-xl font-black text-[#12263A]">Order #{order.id.slice(0, 8)}</h2>
            <p className="text-xs text-neutral-500">Placed on {new Date(order.created_at).toLocaleString('en-GB')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              data-testid="order-print-btn"
              className="bg-[#12263A] hover:bg-black text-white font-bold text-sm px-3 py-1.5 rounded-sm flex items-center gap-1"
            ><Printer className="w-4 h-4" /> Print</button>
            <button
              onClick={() => onReorder(order.id)}
              disabled={reordering}
              data-testid="order-reorder-btn"
              className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
            >
              {reordering ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reorder
            </button>
            <button onClick={onClose} data-testid="order-close-btn" className="p-1.5 text-neutral-500 hover:text-[#12263A] hover:bg-neutral-100 rounded" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable body */}
        <div className="p-6 space-y-5">
          {/* Print-only company header */}
          <div className="only-print text-[#12263A] pb-4 border-b border-neutral-300">
            {settings.logo_url && <img src={settings.logo_url} alt={brandName} className="h-14 mb-2" />}
            <div className="text-2xl font-black">{brandName}</div>
            {settings.company_address && <div className="text-xs whitespace-pre-line">{settings.company_address}</div>}
            {(settings.company_phone || settings.company_email) && (
              <div className="text-xs">
                {settings.company_phone && <span className="mr-3">Tel: {settings.company_phone}</span>}
                {settings.company_email && <span>{settings.company_email}</span>}
              </div>
            )}
            <div className="text-lg font-bold mt-3">Order Receipt #{order.id.slice(0, 8)}</div>
          </div>

          {/* Meta strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase text-neutral-500 font-bold">Status</div>
              <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-sm inline-block mt-1 ${STATUS_COLORS[order.status] || 'bg-neutral-100'}`}>{order.status}</span>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500 font-bold">Order date</div>
              <div className="font-semibold text-[#12263A]">{new Date(order.created_at).toLocaleDateString('en-GB')}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500 font-bold">Payment</div>
              <div className="font-semibold text-[#12263A] capitalize">{order.payment_method || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500 font-bold">Order total</div>
              <div className="font-black text-[#F46A25]">{fmt(grand)}</div>
            </div>
          </div>

          {/* Delivery address */}
          {addr && (addr.name || addr.line1) && (
            <div>
              <div className="text-[10px] uppercase text-neutral-500 font-bold mb-1">Delivery address</div>
              <div className="text-sm text-[#12263A] bg-neutral-50 border border-neutral-200 rounded-sm p-3">
                {addr.name && <div className="font-semibold">{addr.name}</div>}
                {addr.line1 && <div>{addr.line1}</div>}
                {addr.line2 && <div>{addr.line2}</div>}
                {(addr.city || addr.postcode) && <div>{[addr.city, addr.postcode].filter(Boolean).join(', ')}</div>}
                {addr.phone && <div className="text-xs text-neutral-600 mt-1">☎ {addr.phone}</div>}
              </div>
            </div>
          )}

          {/* Line items */}
          <div>
            <div className="text-[10px] uppercase text-neutral-500 font-bold mb-2">Items ({order.items?.length || 0})</div>
            <div className="border border-neutral-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-[10px] uppercase text-neutral-600">
                  <tr>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-right px-3 py-2 w-16">Qty</th>
                    <th className="text-right px-3 py-2 w-24">Unit £</th>
                    <th className="text-right px-3 py-2 w-24">Line £</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((it, idx) => (
                    <tr key={idx} className="border-t border-neutral-100">
                      <td className="px-3 py-2 flex items-center gap-2">
                        <img src={resolveImage(it.image)} alt="" className="w-10 h-10 object-cover rounded-sm shrink-0 hide-in-print-tight" />
                        <div className="min-w-0">
                          <div className="font-semibold text-[#12263A] leading-tight">{it.name}</div>
                          {it.brand && <div className="text-[10px] text-neutral-500">{it.brand}</div>}
                          {it.variant_name && (
                            <div
                              className="text-[10px] text-[#F46A25] font-semibold mt-0.5"
                              data-testid={`order-item-variant-${idx}`}
                            >
                              Variant: {it.variant_name}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="text-right px-3 py-2">{it.qty}</td>
                      <td className="text-right px-3 py-2">{fmt(it.price)}</td>
                      <td className="text-right px-3 py-2 font-bold">{fmt(Number(it.price) * Number(it.qty))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-full md:w-80 text-sm">
              <div className="flex justify-between py-1"><span className="text-neutral-600">Subtotal (ex VAT)</span><span className="font-semibold">{fmt(order.subtotal)}</span></div>
              <div className="flex justify-between py-1"><span className="text-neutral-600">Delivery</span><span className="font-semibold">{Number(order.delivery) === 0 ? 'Free' : fmt(order.delivery)}</span></div>
              <div className="flex justify-between py-1"><span className="text-neutral-600">VAT ({vatRate.toFixed(0)}%)</span><span className="font-semibold">{fmt(vat)}</span></div>
              <div className="flex justify-between py-2 mt-1 border-t border-neutral-200">
                <span className="font-black text-[#12263A]">Total (inc VAT)</span>
                <span className="font-black text-lg text-[#F46A25]">{fmt(grand)}</span>
              </div>
            </div>
          </div>

          {/* Print-only footer */}
          <div className="only-print text-[10px] text-neutral-500 pt-4 border-t border-neutral-200 text-center">
            Thank you for shopping with {brandName}. VAT charged at the prevailing UK rate.
            {settings.website && <> · {settings.website}</>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrderDetailDialog;
